import http from "./http-common";

// Helper: extract a snippet from text around the keyword
function extractSnippet(text, keyword, maxLen = 150) {
  if (!text || !keyword) return "";
  const lower = text.toLowerCase();
  const kLower = keyword.toLowerCase();
  const idx = lower.indexOf(kLower);
  if (idx === -1) return text.substring(0, maxLen) + (text.length > maxLen ? "..." : "");

  const start = Math.max(0, idx - 40);
  const end = Math.min(text.length, idx + keyword.length + maxLen - 40);
  let snippet = text.substring(start, end);
  if (start > 0) snippet = "..." + snippet;
  if (end < text.length) snippet = snippet + "...";
  return snippet;
}

// Helper: find the first matching text from a list of candidate strings
function findMatchingText(candidates, keyword) {
  if (!keyword) return "";
  const kLower = keyword.toLowerCase();
  for (const text of candidates) {
    if (text && text.toLowerCase().includes(kLower)) {
      return extractSnippet(text, keyword);
    }
  }
  // No match found, return first non-empty as fallback
  for (const text of candidates) {
    if (text) return extractSnippet(text, keyword);
  }
  return "";
}

export default new (class SearchService {
  async searchGlobal(keyword) {
    try {
      const response = await http.get(
        `/global-search?q=${encodeURIComponent(keyword)}`,
      );
      const { data } = response.data;
      const lang = window.location.pathname.split("/")[1] || "th";
      const isEN = lang === "en";

      const results = [];

      // Activities
      (data.activities || []).forEach((item) => {
        const title = isEN
          ? item.activity?.event_EN || item.activity?.event_TH
          : item.activity?.event_TH || item.activity?.event_EN;
        const candidates = [
          item.activity?.event_TH, item.activity?.event_EN,
          item.detail?.detail_TH, item.detail?.detail_EN,
        ];
        results.push({
          title: title || (isEN ? "Activity" : "กิจกรรม"),
          category: isEN ? "Activities" : "กิจกรรม",
          snippet: findMatchingText(candidates, keyword),
          link: `/${lang}/newsAndActivity/activity/${item.Slug || item.id}`,
        });
      });

      // News
      (data.news || []).forEach((item) => {
        const title = isEN
          ? item.title?.detail_EN || item.title?.detail_TH
          : item.title?.detail_TH || item.title?.detail_EN;
        const candidates = [
          item.title?.detail_TH, item.title?.detail_EN,
          item.description?.detail_TH, item.description?.detail_EN,
        ];
        results.push({
          title: title || (isEN ? "News" : "ข่าวสาร"),
          category: isEN ? "News" : "ข่าวสาร",
          snippet: findMatchingText(candidates, keyword),
          link: `/${lang}/newsAndActivity/news/${item.Slug || item.id}`,
        });
      });

      // Articles — ใน Strapi ชื่อ field คือ Title (T ใหญ่)
      (data.articles || []).forEach((item) => {
        const t = item.Title || item.title;
        const title = isEN
          ? t?.detail_EN || t?.detail_TH
          : t?.detail_TH || t?.detail_EN;
        const candidates = [
          t?.detail_TH, t?.detail_EN,
          item.description?.detail_TH, item.description?.detail_EN,
        ];
        results.push({
          title: title || (isEN ? "Article" : "บทความ"),
          category: isEN ? "Articles" : "บทความ",
          snippet: findMatchingText(candidates, keyword),
          link: `/${lang}/article/${item.Slug || item.id}`,
        });
      });

      // Infographics
      (data.infographics || []).forEach((item) => {
        const title = isEN
          ? item.Title?.detail_EN || item.Title?.detail_TH
          : item.Title?.detail_TH || item.Title?.detail_EN;
        results.push({
          title: title || (isEN ? "Infographic" : "อินโฟกราฟิก"),
          category: isEN ? "Infographics" : "อินโฟกราฟิก",
          snippet: findMatchingText(
            [item.Title?.detail_TH, item.Title?.detail_EN, item.description?.detail_TH, item.description?.detail_EN],
            keyword
          ),
          link: `/${lang}/infographic/${item.Slug || item.id}`,
        });
      });

      // Videos
      (data.videos || []).forEach((item) => {
        const title = isEN
          ? item.title?.detail_EN || item.title?.detail_TH
          : item.title?.detail_TH || item.title?.detail_EN;
        results.push({
          title: title || (isEN ? "Video" : "วิดีโอ"),
          category: isEN ? "Videos" : "วิดีโอ",
          snippet: findMatchingText([item.title?.detail_TH, item.title?.detail_EN], keyword),
          link: `/${lang}/newsAndActivity`,
        });
      });

      // Galleries
      (data.galleries || []).forEach((item) => {
        const title = isEN
          ? item.title?.detail_EN || item.title?.detail_TH
          : item.title?.detail_TH || item.title?.detail_EN;
        results.push({
          title: title || (isEN ? "Gallery" : "ประมวลภาพ"),
          category: isEN ? "Gallery" : "ประมวลภาพกิจกรรม",
          snippet: findMatchingText([item.title?.detail_TH, item.title?.detail_EN], keyword),
          link: `/${lang}/newsAndActivity`,
        });
      });

      // Services
      (data.services || []).forEach((item) => {
        const title = isEN
          ? item.link?.label_EN || item.link?.label_TH
          : item.link?.label_TH || item.link?.label_EN;
        const candidates = [
          item.link?.label_TH, item.link?.label_EN,
          item.detail?.detail_TH, item.detail?.detail_EN,
        ];
        results.push({
          title: title || (isEN ? "Service" : "บริการ"),
          category: isEN ? "Services" : "บริการ",
          snippet: findMatchingText(candidates, keyword),
          link: `/${lang}/service/${item.link?.href || ""}`,
        });
      });

      // Documents
      (data.documents || []).forEach((item) => {
        const title = isEN
          ? item.documentType?.detail_EN || item.documentType?.detail_TH
          : item.documentType?.detail_TH || item.documentType?.detail_EN;
        results.push({
          title: title || (isEN ? "Document" : "เอกสาร"),
          category: isEN ? "Documents" : "เอกสารเผยแพร่",
          snippet: findMatchingText([item.documentType?.detail_TH, item.documentType?.detail_EN], keyword),
          link: `/${lang}/document`,
        });
      });

      // About Us
      (data.abouts || []).forEach((item) => {
        const title = isEN
          ? item.section_title_EN || item.section_title_TH
          : item.section_title_TH || item.section_title_EN;
        // Gather text from Content dynamic zone
        const candidates = [item.section_title_TH, item.section_title_EN];
        if (item.Content && Array.isArray(item.Content)) {
          item.Content.forEach(c => {
            candidates.push(c.title_TH, c.title_EN, c.description_TH, c.description_EN, c.detail_TH, c.detail_EN);
          });
        }
        results.push({
          title: title || (isEN ? "About Us" : "เกี่ยวกับเรา"),
          category: isEN ? "About Us" : "เกี่ยวกับเรา",
          snippet: findMatchingText(candidates, keyword),
          link: `/${lang}/about-us`,
        });
      });

      // Staff
      (data.staffs || []).forEach((staffGroup) => {
        const groupTitle = isEN
          ? staffGroup.field_name?.detail_EN || staffGroup.field_name?.detail_TH
          : staffGroup.field_name?.detail_TH || staffGroup.field_name?.detail_EN;
        const candidates = [staffGroup.field_name?.detail_TH, staffGroup.field_name?.detail_EN];
        if (staffGroup.staff && Array.isArray(staffGroup.staff)) {
          staffGroup.staff.forEach(s => {
            candidates.push(s.name_TH, s.name_EN, s.role_TH, s.role_EN, s.email);
          });
        }
        results.push({
          title: groupTitle || (isEN ? "Staff" : "บุคลากร"),
          category: isEN ? "Staff" : "บุคลากร",
          snippet: findMatchingText(candidates, keyword),
          link: `/${lang}/staff`,
        });
      });

      // FAQs
      (data.faqs || []).forEach((item) => {
        const title = isEN
          ? item.Question?.detail_EN || item.Question?.detail_TH
          : item.Question?.detail_TH || item.Question?.detail_EN;
        const candidates = [item.Question?.detail_TH, item.Question?.detail_EN];
        if (item.Answer && Array.isArray(item.Answer)) {
          item.Answer.forEach(a => {
            candidates.push(a.detail_TH, a.detail_EN);
          });
        }
        results.push({
          title: title || (isEN ? "FAQ" : "คำถามที่พบบ่อย"),
          category: isEN ? "FAQ" : "คำถามที่พบบ่อย",
          snippet: findMatchingText(candidates, keyword),
          link: `/${lang}/faq`,
        });
      });

      // Contacts
      (data.contacts || []).forEach((item) => {
        const title = isEN
          ? item.title?.detail_EN || item.title?.detail_TH || item.section
          : item.title?.detail_TH || item.title?.detail_EN || item.section;
        const candidates = [item.title?.detail_TH, item.title?.detail_EN];
        if (item.content && Array.isArray(item.content)) {
          item.content.forEach(c => {
            candidates.push(c.detail_TH, c.detail_EN, c.title_TH, c.title_EN);
          });
        }
        results.push({
          title: title || (isEN ? "Contact Us" : "ติดต่อเรา"),
          category: isEN ? "Contact Us" : "ติดต่อเรา",
          snippet: findMatchingText(candidates, keyword),
          link: `/${lang}/contact-us`,
        });
      });

      // Appointments
      (data.appointments || []).forEach((item) => {
        const title = isEN
          ? item.name?.detail_EN || item.name?.detail_TH
          : item.name?.detail_TH || item.name?.detail_EN;
        const candidates = [
          item.name?.detail_TH, item.name?.detail_EN,
          item.detail?.detail_TH, item.detail?.detail_EN,
        ];
        results.push({
          title: title || (isEN ? "Appointment" : "บริการนัดหมาย"),
          category: isEN ? "Appointment Services" : "บริการนัดหมาย",
          snippet: findMatchingText(candidates, keyword),
          link: `/${lang}/appointment`,
        });
      });

      return { results, totalCount: results.length };
    } catch (error) {
      console.error(error);
      return { results: [], totalCount: 0 };
    }
  }
})();
